<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
  <SamMenu
    ref="selectMenu"
    class="sam-select"
    :propagate-click="propagateClick"
  >
    <template #activator="{ on, isMenuOpen, toggle }">
      <a
        :class="[
          `sam-select__menu-activator`,
          { 'is-disabled': disabled },
          { 'is-readonly': readonly },
          { 'as-field': asField },
        ]"
        v-on="disabled ? {} : on"
        @click.prevent
      >
        <SamInput
          v-bind="$attrs"
          class="sam-select__input-field"
          :value="selection.label"
          :clearable="clearable"
          :disabled="disabled"
          readonly
          :as-field="asField"
          :placeholder="placeholder"
          @click.prevent="toggle"
          @clear="clearSelection"
        >
          <template #prepend>
            <SamIcon
              v-if="selection.icon"
              :icon="selection.icon"
              :color="disabled ? '#919498' : 'currentColor'"
            />
          </template>

          <template #append>
            <SamIcon
              class="sam-select__expand-icon"
              :icon="isMenuOpen ? 'arrow-chevron-up' : 'arrow-chevron-down'"
              size="sm"
            />
          </template>
        </SamInput>
      </a>
    </template>

    <template v-if="!parsedItems.length">
      <div
        v-if="!$slots['no-data']"
        class="sam-select__no-data"
      >
        No data to show
      </div>
      <template v-else>
        <slot name="no-data" />
      </template>
    </template>
    <SamTextList
      v-else
      ref="list"
      :items="parsedItems"
    >
      <template #default="{ item, index }">
        <SamTextListItem
          :item="item"
          :data-key-index="index"
          :bablic-exclude="dontTranslateItems"
          @click="selectOption(item)"
        />
      </template>
    </SamTextList>
  </SamMenu>
</template>

<script>
import SamMenu from '../SamMenu/SamMenu.vue';
import SamInput from '../SamInput/SamInput.vue';
import SamTextList from '../SamTextList/SamTextList.vue';
import SamIcon from '../SamIcon/SamIcon.vue';
import { debounce, objectHasOwnProperties } from '../../../utils/utils';
import SamTextListItem from '../SamTextList/SamTextListItem.vue';
import SAMORA_STYLES from '../../../utils/constants/samora-styles.enum';

export default {
  name: 'SamSelect',
  components: {
    SamTextListItem,
    SamTextList,
    SamMenu,
    SamInput,
    SamIcon,
  },
  props: {
    /**
     * The Select options for the dropdown
     */
    items: {
      type: Array,
      required: true,
      default: () => [],
      validator: (val) => val.every?.((item) => objectHasOwnProperties(item, 'label', 'value')),
    },
    /**
     * The Select value
     */
    value: {
      type: [Number, String],
      required: false,
      default: undefined,
    },
    /**
     * If the Select should display a clear input button
     * @values true, false
     */
    withClear: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * The default selected value when the select box is cleared
     */
    defaultValue: {
      type: [Number, String, null],
      required: false,
      default: null,
    },
    /**
     * If the Select is disabled (html attribute disabled)
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the Select is readonly
     * @values true, false
     */
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the Select is to be displayed as field
     * @values true, false
     */
    asField: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the user can search Select values using his keyboard
     * @values true, false
     */
    isSearchable: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * The input's placeholder.
     */
    placeholder: {
      type: String,
      default: null,
    },
    /**
     * avoid $event.stopPropagation() on menu activator toggle. Use it if you have
     * a menu aside with other menus and the menu content overlaps eachother when
     * opened.
     */
    propagateClick: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If you wish to exclude items text from translation
     */
    dontTranslateItems: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      search: '',
      debouncedClearSearch: null,
    };
  },
  computed: {
    selection: {
      get() {
        return (
          this.items.find?.((i) => i.value === this.value) ?? {
            label: null,
            value: null,
          }
        );
      },
      set(option) {
        /**
         * Triggers when Select changes it's value
         * @event input
         * @property {string} value new value set
         */
        this.$emit('input', option?.value);
        /**
         * Triggers when Select changes it's value
         * @event change
         * @property {TextListItem} option the selected option
         */
        this.$emit('change', option);
      },
    },
    parsedItems() {
      if (!this.items) {
        return [];
      }
      return this.items.map((item) => {
        let samStyle = SAMORA_STYLES.DEFAULT;
        if (item.value === this.value) {
          samStyle = SAMORA_STYLES.SUCCESS;
        } else if (item.samStyle) {
          samStyle = item.samStyle;
        }
        return { ...item, samStyle };
      });
    },
    clearable() {
      return this.withClear && this.value !== this.defaultValue;
    },
  },
  created() {
    this.debouncedClearSearch = debounce(this.clearSearch, 500);
    if (!this.value && !!this.defaultValue) {
      this.selection = this.defaultValue;
    }
  },
  mounted() {
    this.$refs.selectMenu.$el.addEventListener('keydown', this.keyPress, true);
  },
  beforeDestroy() {
    this.$refs.selectMenu.$el.addEventListener('keydown', this.keyPress, true);
  },
  methods: {
    selectOption(option) {
      this.selection = option;
    },
    clearSelection($event) {
      if ($event) {
        $event.stopPropagation();
      }
      if (this.disabled) return;
      this.selection = this.parsedItems.find((i) => i.value === this.defaultValue) ?? {
        label: null,
        value: null,
      };
    },
    keyPress(ev) {
      ev.preventDefault();

      if (!this.$refs.list) return;

      const currIndex = document.activeElement.dataset.keyIndex ?? null;
      const elemCount = this.$refs.list.$el?.childElementCount ?? 0;

      if (ev.key === 'ArrowDown') {
        let index = 0;

        if (currIndex !== null) {
          index = parseInt(currIndex, 10);
          index += 1;
        }

        if (index <= elemCount - 1) {
          // eslint-disable-next-line no-unused-expressions
          this.$refs.list?.$el?.querySelector?.(`[data-key-index="${index}"]`)?.focus?.();
        }
      } else if (ev.key === 'ArrowUp') {
        let index = currIndex !== null ? parseInt(currIndex, 10) : 0;
        index -= 1;
        if (index >= 0) {
          // eslint-disable-next-line no-unused-expressions
          this.$refs.list?.$el?.querySelector?.(`[data-key-index="${index}"]`)?.focus?.();
        }
      } else if (ev.key === 'Escape') {
        document.activeElement.blur();
      } else if (ev.key === 'Backspace' && this.withClear) {
        this.clearSelection(ev);
      } else if (ev.key === 'Enter') {
        this.selection = this.items?.[parseInt(currIndex, 10)] ?? null;
        // eslint-disable-next-line no-unused-expressions
        this.$refs.selectMenu?.close?.();
      } else if (ev.key !== 'Enter' && this.isSearchable) {
        this.searchPerText(ev.key);
      }
    },
    searchPerText(newLetter) {
      const newSearch = this.search + newLetter.toLowerCase();
      const index = this.items.findIndex((item) => item.label.toLowerCase().startsWith(newSearch));

      // eslint-disable-next-line no-unused-expressions
      this.debouncedClearSearch?.();

      if (index === -1) {
        // eslint-disable-next-line no-unused-expressions
        this.$refs.selectMenu?.$el?.focus?.();
        return;
      }

      this.search = newSearch;
      // eslint-disable-next-line no-unused-expressions
      this.$refs.list?.$el?.querySelector?.(`[data-key-index="${index}"]`)?.focus?.();
    },
    clearSearch() {
      this.search = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-select::v-deep {
  width: 100%;

  .sam-select__expand-icon.sam-icon {
    pointer-events: none;
  }

  .sam-select__menu-activator {
    cursor: pointer;

    .sam-input__input,
    .sam-select__input-field {
      cursor: pointer;
    }

    &.is-disabled {
      cursor: not-allowed;

      .sam-select__expand-icon {
        color: $neutral--50;
      }
    }

    &.is-readonly,
    &.as-field {
      pointer-events: none;

      .sam-select__expand-icon {
        display: none;
      }
    }
  }

  .menu__content {
    max-height: calc($size--14 * 4 + $size--7); // 4 list items + half
    overflow-y: auto;
  }

  .sam-select__no-data {
    @extend .text--caption, .p-4;
    color: $neutral--60;
    font-style: italic;
    text-align: center;
  }
}
</style>
